// The life vocabulary (IMP-0033): the conditions, actions and scorers a non-combat behaviour tree may name, for
// townsfolk on their routines and animals about a home. It sits beside the combat vocabulary (brains.ts) on the same
// tree runner (bt.ts) and needs none of combat's ports: no hero, no attack tokens, no combat port. Each word reads one
// actor's view of its day (`LifeContext`) and acts only through ports the game hands it: a mover (the same
// `BrainMover` the combat brains use: a crowd, a steering layer), the routine's decision for this step (routines.ts),
// the game's places, and an optional cue port for waves, calls and barks.
//
// The words, by what a townsperson or an animal does:
// - routine steps: `onRoutine`, `atRoutine`, `activity` and `followRoutine` (walk to the block's spot, face its way,
//   do its activity);
// - go to a place and idle at a spot: `atPlace`, `goTo`, `idle`, `wander` (free time; the combat vocabulary's own
//   `wander`, shared so a merged registry has one);
// - talk when spoken to: `spokenTo`, `talk`;
// - react to the player's presence: `playerNear`, `noticed` (perception's levels), `watchPlayer`, `approachPlayer`,
//   `emote`;
// - keep a distance and follow: `keepAway`, `follow`;
// - rest by need: `needBelow`, `needAbove`, `restUntil`, and the `urgency` scorer for utility choices.
//
// Every action writes `e.doing`, what the body should show now ("walk", "run", "idle", "talk", "watch", the routine's
// activity, a rest's activity), so the game's figure and its needs model read one field. The game owns the numbers
// (speeds, distances, thresholds come from the tree's JSON and the profile) and the needs model (`restUntil` raises a
// need itself only when given a `rate`). Nothing here draws random numbers (the context's `random` is the game's
// seeded stream) or reads the clock (`dt` comes in). No word's name collides with a combat word's except `wander`,
// which is the same function, so a game may merge both registries for an actor that lives and fights.
import type { BehaviourRegistry, Status } from "./bt";
import type { PerceptionState, Vec3 } from "./perception";
import { arriveRadius, type PlaceSpot, type RoutineDecision, type RoutinePlace } from "./routines";
import { standardBrains, type BrainMover } from "./brains";

/** The actor a life brain drives: the fields the vocabulary reads and writes (a game's saved NPC or animal has these
 *  and more). Shares its names with `BrainActor` where they mean the same. */
export interface LifeActor {
  /** Its id in the mover. */
  id: number;
  /** Where it wanders around. */
  home: Vec3;
  pos: Vec3;
  /** Yaw (radians, 0 faces +z). */
  facing: number;
  /** The point it is wandering to, and how long it still waits there (`wander`'s). */
  wanderAt: Vec3 | null;
  wanderWait: number;
  /** What its body should show now, written by the actions: "walk", "run", "idle", "talk", "watch", the routine's
   *  activity, a rest's activity; null before any action ran. */
  doing: string | null;
  /** Its needs, 0 (empty) to 1 (met), by name ("energy", "hunger", "company"): the game's needs model keeps them. */
  needs?: Record<string, number>;
  /** Its perception, when the game runs one for it (`noticed` reads the level). */
  percept?: PerceptionState;
}

/** An actor kind's numbers as the vocabulary reads them. */
export interface LifeProfile {
  /** Walking and running speeds (m/s) and the turn rate (radians/s). */
  speed: { walk: number; run: number; turn: number };
}

/** What a life brain sees and can do, for one actor on one tick. */
export interface LifeContext<A extends LifeActor = LifeActor, P extends LifeProfile = LifeProfile> {
  e: A;
  arch: P;
  /** Speed scale now (a mood's, the weather's): scales walking, running and turning. */
  speedScale: number;
  dt: number;
  crowd: BrainMover;
  /** The player (whoever the actor notices, follows or keeps away from), or null when there is none near. */
  player: { pos: Vec3 } | null;
  /** Who is talking to it now, or null: the game sets it on an interaction and clears it when the talk closes. */
  speaker: { pos: Vec3 } | null;
  /** Its routine's decision this step (`stepRoutines`), or null when it has no routine. */
  routine: RoutineDecision | null;
  /** A place by id for this actor (the game resolves "home" per actor), or null. */
  place(id: string): RoutinePlace | null;
  /** A point on the walkable ground near `p`, or null. */
  snap(p: Vec3): Vec3 | null;
  /** A seeded number in [0, 1) (the game's saved stream). */
  random(): number;
  /** Play a momentary cue on the actor (a wave, a call, a bark): the game's figure and audio. Absent: cues are dropped. */
  cue?(id: number, cue: string): void;
}

/** Planted defects (switched by a game's or a test's checks; each beside what it breaks). */
export const LIFE_FAULTS = {
  /** `spokenTo` never holds: an NPC walks on while the player talks to it (the talk check must go red). */
  talkIgnored: false,
  /** `playerNear` never holds: an NPC ignores a passer-by, with no wave and no look (the presence check must go red). */
  playerUnseen: false,
  /** `follow` keeps aiming at where the player was when it began, never re-aiming: the follower falls behind (the
   *  follow-distance check must go red). */
  followStale: false,
  /** `needBelow` never holds and `restUntil` never starts: a tired animal never rests (the rest-by-need check must go
   *  red). */
  needIgnored: false,
  /** `keepAway` never backs off: a shy animal lets the player walk up to it (the keep-a-distance check must go red). */
  distanceIgnored: false,
};

type LifeAction = (b: LifeContext, a: Record<string, unknown>, m: Record<string, unknown>, dt: number) => Status;

const dist = (a: Vec3, b: Vec3): number => Math.hypot(a[0] - b[0], a[2] - b[2]);
const yawTo = (a: Vec3, b: Vec3): number => Math.atan2(b[0] - a[0], b[2] - a[2]);
const wrap = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a));
const num = (v: unknown, d: number): number => (typeof v === "number" ? v : d);
const walkSpeed = (b: LifeContext): number => b.arch.speed.walk * b.speedScale;
const runSpeed = (b: LifeContext): number => b.arch.speed.run * b.speedScale;
/** A spot on the ground (the mover's goal): snapped near the actor's height, or as it is. */
const ground = (b: LifeContext, x: number, z: number): Vec3 => b.snap([x, b.e.pos[1], z]) ?? [x, b.e.pos[1], z];
const LEVELS = { idle: 0, suspicious: 1, alert: 2 } as const;
/** A need's value (absent: met, 1). */
const needOf = (b: LifeContext, name: unknown): number => {
  const v = typeof name === "string" ? b.e.needs?.[name] : undefined;
  return typeof v === "number" ? v : 1;
};

/** Turn toward `yaw` at the profile's turn rate (scaled); returns the angle left. */
export function lifeTurnToward(b: LifeContext, yaw: number): number {
  const d = wrap(yaw - b.e.facing);
  const step = b.arch.speed.turn * b.speedScale * b.dt;
  b.e.facing = wrap(b.e.facing + Math.max(-step, Math.min(step, d)));
  return Math.abs(wrap(yaw - b.e.facing));
}

/** Walk to `spot` and face its way: "success" once there and facing within `tolerance`, else "running". */
function walkToSpot(b: LifeContext, spot: PlaceSpot, arrive: number, run: boolean, tolerance: number): Status {
  if (Math.hypot(b.e.pos[0] - spot.x, b.e.pos[2] - spot.z) > arrive) {
    b.crowd.moveTo(b.e.id, ground(b, spot.x, spot.z), 0.15, run ? runSpeed(b) : walkSpeed(b));
    b.e.doing = run ? "run" : "walk";
    return "running";
  }
  b.crowd.stop(b.e.id);
  b.e.doing = "idle";
  if (spot.yaw === undefined) return "success";
  return lifeTurnToward(b, spot.yaw) <= tolerance ? "success" : "running";
}

/** The spot of `place` an action uses: index `spot` when given (wrapped), else the nearest to the actor. */
function spotOf(b: LifeContext, place: RoutinePlace, spot: unknown): number {
  const n = place.spots.length;
  if (typeof spot === "number") return ((Math.floor(spot) % n) + n) % n;
  let best = 0;
  let bd = Infinity;
  place.spots.forEach((s, i) => {
    const d = Math.hypot(b.e.pos[0] - s.x, b.e.pos[2] - s.z);
    if (d < bd - 1e-9) {
      bd = d;
      best = i;
    }
  });
  return best;
}

const sharedWander = standardBrains().actions.wander as unknown as LifeAction;

/** The life vocabulary. Arguments come from the tree's JSON (defaults in brackets). */
const LIFE: BehaviourRegistry<LifeContext> = {
  conditions: {
    /** Its routine has somewhere to be now (a block with a place it has). */
    onRoutine: (b) => b.routine?.goal != null,
    /** It has arrived at its routine's spot. */
    atRoutine: (b) => b.routine?.goal != null && b.routine.arrived,
    /** Its routine's activity now is `is`, or one of `in` (a list: the interruptible ones, say). */
    activity: (b, a) => {
      const act = b.routine?.activity ?? null;
      if (act === null) return false;
      if (typeof a.is === "string") return act === a.is;
      return Array.isArray(a.in) && a.in.includes(act);
    },
    /** It stands within `within` [its arrival radius] m of a spot of `place`. */
    atPlace: (b, a) => {
      const p = typeof a.place === "string" ? b.place(a.place) : null;
      if (!p) return false;
      const r = num(a.within, arriveRadius(p));
      return p.spots.some((s) => Math.hypot(b.e.pos[0] - s.x, b.e.pos[2] - s.z) <= r);
    },
    /** Someone is talking to it. */
    spokenTo: (b) => !LIFE_FAULTS.talkIgnored && b.speaker !== null,
    /** The player is within `within` [4] m. */
    playerNear: (b, a) => !LIFE_FAULTS.playerUnseen && b.player !== null && dist(b.e.pos, b.player.pos) <= num(a.within, 4),
    /** Its perception is at `level` ["suspicious"] or higher ("alert"); false without perception. */
    noticed: (b, a) => {
      const lv = b.e.percept?.level;
      if (!lv) return false;
      const want = a.level === "alert" ? LEVELS.alert : LEVELS.suspicious;
      return LEVELS[lv] >= want;
    },
    /** Need `need` is below `below` [0.3]. */
    needBelow: (b, a) => !LIFE_FAULTS.needIgnored && needOf(b, a.need) < num(a.below, 0.3),
    /** Need `need` is at or above `above` [0.7]. */
    needAbove: (b, a) => needOf(b, a.need) >= num(a.above, 0.7),
  },
  actions: {
    /** Follow the routine: walk to the block's spot, then stand facing its way doing the block's activity (running);
     *  "failure" in free time (no block, or no such place), so the tree's next branch decides. */
    followRoutine: (b) => {
      const r = b.routine;
      if (!r || !r.goal) return "failure";
      if (r.arrived || r.snap) {
        b.crowd.stop(b.e.id);
        if (r.goal.yaw !== undefined && !r.inside) lifeTurnToward(b, r.goal.yaw);
        b.e.doing = r.activity;
        return "running";
      }
      b.crowd.moveTo(b.e.id, ground(b, r.goal.x, r.goal.z), 0.15, walkSpeed(b));
      b.e.doing = "walk";
      return "running";
    },
    /** Walk (or `run` [false]) to a spot of `place` (index `spot`, else the nearest when it set off) and face the
     *  spot's way: "success" there; "failure" when the place is unknown. */
    goTo: (b, a, m) => {
      const p = typeof a.place === "string" ? b.place(a.place) : null;
      if (!p || !p.spots.length) return "failure";
      if (typeof m.spot !== "number") m.spot = spotOf(b, p, a.spot);
      return walkToSpot(b, p.spots[(m.spot as number) % p.spots.length]!, arriveRadius(p), a.run === true, 0.3);
    },
    /** Stand still: running, or "success" after `seconds` when given. */
    idle: (b, a, m) => {
      b.crowd.stop(b.e.id);
      b.e.doing = "idle";
      if (typeof a.seconds !== "number") return "running";
      m.t = num(m.t, 0) + b.dt;
      return (m.t as number) >= a.seconds - 1e-9 ? "success" : "running";
    },
    /** Stroll between seeded points within `radius` [3] m of home, pausing `pause` [2] s at each (running): the
     *  combat vocabulary's own `wander`, writing `doing`. */
    wander: (b, a, m, dt) => {
      const s = sharedWander(b, a, m, dt);
      b.e.doing = b.e.wanderWait > 0 || !b.e.wanderAt ? "idle" : "walk";
      return s;
    },
    /** Talk with whoever is speaking to it: stand, face them (running); "success" once they've gone. */
    talk: (b) => {
      if (!b.speaker) return "success";
      b.crowd.stop(b.e.id);
      lifeTurnToward(b, yawTo(b.e.pos, b.speaker.pos));
      b.e.doing = "talk";
      return "running";
    },
    /** Stand and turn to watch the player (running); "failure" when there is none. */
    watchPlayer: (b) => {
      if (!b.player) return "failure";
      b.crowd.stop(b.e.id);
      lifeTurnToward(b, yawTo(b.e.pos, b.player.pos));
      b.e.doing = "watch";
      return "running";
    },
    /** Walk (or `run` [false]) over to the player until within `stop` [1.5] m, then face them: "success" there;
     *  "failure" when there is none. */
    approachPlayer: (b, a) => {
      if (!b.player) return "failure";
      const stop = num(a.stop, 1.5);
      if (dist(b.e.pos, b.player.pos) > stop) {
        b.crowd.moveTo(b.e.id, b.player.pos, stop * 0.9, a.run === true ? runSpeed(b) : walkSpeed(b));
        b.e.doing = a.run === true ? "run" : "walk";
        return "running";
      }
      b.crowd.stop(b.e.id);
      b.e.doing = "idle";
      return lifeTurnToward(b, yawTo(b.e.pos, b.player.pos)) <= 0.3 ? "success" : "running";
    },
    /** Play cue `cue` ["wave"] on the actor (the game's figure and audio): "success". Wrap it in a cooldown to space
     *  them out. */
    emote: (b, a) => {
      b.cue?.(b.e.id, typeof a.cue === "string" ? a.cue : "wave");
      return "success";
    },
    /** Keep `min` [4] m from the player: once the player is closer, move off (running, or walking with `run` false)
     *  to `min` + `margin` [1] m; "success" on reaching it; "failure" when there's nothing to keep away from. */
    keepAway: (b, a, m) => {
      if (!b.player || LIFE_FAULTS.distanceIgnored) return "failure";
      const min = num(a.min, 4);
      const far = min + num(a.margin, 1);
      const d = dist(b.e.pos, b.player.pos);
      if (!m.away && d >= min) return "failure";
      if (d >= far - 0.05) {
        b.crowd.stop(b.e.id);
        b.e.doing = "idle";
        return "success";
      }
      m.away = true;
      const yaw = d > 1e-6 ? yawTo(b.player.pos, b.e.pos) : b.e.id % 2 === 0 ? 0 : Math.PI;
      const run = a.run !== false;
      b.crowd.moveTo(b.e.id, ground(b, b.player.pos[0] + Math.sin(yaw) * far, b.player.pos[2] + Math.cos(yaw) * far), 0.1, run ? runSpeed(b) : walkSpeed(b));
      b.e.doing = run ? "run" : "walk";
      return "running";
    },
    /** Follow the player at `distance` [2.5] m, re-aiming every tick, running when more than `runBeyond` [6] m
     *  behind (running); "failure" when there is none. */
    follow: (b, a, m) => {
      if (!b.player) return "failure";
      const at: Vec3 = LIFE_FAULTS.followStale ? ((m.at as Vec3 | undefined) ?? (m.at = [...b.player.pos] as Vec3)) : b.player.pos;
      const gap = num(a.distance, 2.5);
      const d = dist(b.e.pos, at);
      if (d <= gap) {
        b.crowd.stop(b.e.id);
        b.e.doing = "idle";
        return "running";
      }
      const run = d > num(a.runBeyond, 6);
      b.crowd.moveTo(b.e.id, at, gap * 0.9, run ? runSpeed(b) : walkSpeed(b));
      b.e.doing = run ? "run" : "walk";
      return "running";
    },
    /** Rest by need: once need `need` falls below `below` [0.3], (walk to `place` when given, then) rest doing
     *  `activity` ["rest"] until it reaches `until` [0.9] ("success"); "failure" while it isn't needed. The game's
     *  needs model refills the need while `doing` is the activity, or the action does at `rate` per second when given. */
    restUntil: (b, a, m) => {
      const v = needOf(b, a.need);
      if (!m.resting) {
        if (LIFE_FAULTS.needIgnored || v >= num(a.below, 0.3)) return "failure";
        m.resting = true;
      }
      if (v >= num(a.until, 0.9)) return "success";
      const p = typeof a.place === "string" ? b.place(a.place) : null;
      if (p && p.spots.length) {
        if (typeof m.spot !== "number") m.spot = spotOf(b, p, a.spot);
        if (walkToSpot(b, p.spots[(m.spot as number) % p.spots.length]!, arriveRadius(p), false, 0.3) === "running" && b.e.doing !== "idle") return "running";
      }
      b.crowd.stop(b.e.id);
      b.e.doing = typeof a.activity === "string" ? a.activity : "rest";
      if (typeof a.rate === "number" && typeof a.need === "string" && b.e.needs) b.e.needs[a.need] = Math.min(1, v + a.rate * b.dt);
      return "running";
    },
  },
  scorers: {
    /** How pressing need `need` is: `weight` [1] × (1 − its value); 0 when the actor doesn't track it. */
    urgency: (b, a) => {
      const v = typeof a.need === "string" ? b.e.needs?.[a.need] : undefined;
      return typeof v === "number" ? num(a.weight, 1) * Math.max(0, Math.min(1, 1 - v)) : 0;
    },
    /** How close the player is: `weight` [1] at the actor, falling to 0 at `within` [10] m; 0 without a player. */
    closeness: (b, a) => (b.player ? num(a.weight, 1) * Math.max(0, 1 - dist(b.e.pos, b.player.pos) / num(a.within, 10)) : 0),
    /** A fixed score, `weight` [1]: the fallback option of a utility node. */
    flat: (_b, a) => num(a.weight, 1),
  },
};

/** The life vocabulary, typed for a game's own context (which has at least `LifeContext`'s fields). */
export function lifeBrains<C extends LifeContext = LifeContext>(): BehaviourRegistry<C> {
  return LIFE as unknown as BehaviourRegistry<C>;
}

/** The names the life vocabulary registers (validate a tree against them with `validateTree`). */
export const LIFE_BRAIN_NAMES = {
  conditions: Object.keys(LIFE.conditions),
  actions: Object.keys(LIFE.actions),
  scorers: Object.keys(LIFE.scorers ?? {}),
};
